import { z } from "zod";

import { SenderSchema } from "./sender.schema";

/**
 * A list's outreach configuration (PREC-3687) — the settings the wizard's Outreach step owns.
 *
 * Lives in its own module rather than in `schema/list.ts`: that file is at the length ratchet
 * ceiling and may only shrink. Follows `event-outreach.schema.ts` / `list-signal-config.ts`.
 *
 * The API always answers with a complete config (a list with no stored row is served its
 * source-type defaults), so nothing here is optional on the read side.
 *
 * There is deliberately no `chatPreferredChannel`: generation runs both channels and which draft
 * opens first is a frontend concern (feature definition §9 / O2).
 */
export const ListOutreachConfigSchema = z.object({
	/** Whether campaigns can be used on this list at all. Defaults off for `TRACKER`. */
	campaignsEnabled: z.boolean(),
	/** Shared instruction; sent as the generation DTO's `userInstruction`. Empty is valid. */
	copywritingInstruction: z.string(),
	/** The company template currently loaded, for the `<name> loaded` label. */
	copywritingInstructionTemplateId: z.number().nullable(),
	/** Auto-generate chat drafts for qualified leads and signals. Defaults on for `TRACKER`. */
	autoCreateAiCopy: z.boolean(),
	/** ISO code for generated chat copy; `null` = Auto. */
	chatLanguage: z.string().nullable(),
	/** Forced copywriting framework; `null` = Auto-suggest. */
	chatFrameworkId: z.number().nullable(),
	/** The sender pool auto-generation draws from — the same shape a campaign stores. */
	chatSenders: z.array(SenderSchema),
	/** Free-text rules for picking a sender out of the pool. */
	senderRoutingInstruction: z.string(),
	senderRoutingTemplateId: z.number().nullable(),
	/**
	 * Whether chat copywriting for a lead on this list is already credit-exempt (D11). Derived by
	 * the API from the source type, never stored and never patchable — the Chat section's credit
	 * label reads it instead of re-deriving the rule in the browser.
	 */
	isCopywritingIncluded: z.boolean(),
});

export type ListOutreachConfig = z.infer<typeof ListOutreachConfigSchema>;

/**
 * The PATCH body. Every field is optional and only the ones present are written, so the step can
 * autosave one control at a time; an explicit `null` is a value (Auto / no template), not an
 * omission. `isCopywritingIncluded` is omitted: it is derived server-side, so sending it back
 * would be a write to something that has no column.
 */
export const UpdateListOutreachConfigSchema = ListOutreachConfigSchema.omit({
	isCopywritingIncluded: true,
}).partial();

export type UpdateListOutreachConfig = z.infer<typeof UpdateListOutreachConfigSchema>;
